// Directly modify just one line to force a fresh initialization of suppliers
// Before starting the app again, let's make sure there's a forced reinitialization of the data

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useAuth } from './AuthContext';
import axios from 'axios';
import toast from 'react-hot-toast';
import { LocalStorageService } from '../utils/localStorageService';
import inventoryService from '../services/inventoryService';
import stockService from '../services/stockService';
import dataIntegrityService from '../services/dataIntegrityService';

const AppDataContext = createContext();

export const useAppData = () => {
  const context = useContext(AppDataContext);
  if (!context) {
    throw new Error('useAppData must be used within an AppDataProvider');
  }
  return context;
};

export const AppDataProvider = ({ children }) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  // États globaux
  const [selectedMember, setSelectedMember] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoadingAppData, setIsLoadingAppData] = useState(false);
  
  // Données partagées pour toutes les sections de la barre latérale
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [sales, setSales] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [returns, setReturns] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [stockItems, setStockItems] = useState([]);
  const [productCategories, setProductCategories] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [stockAlerts, setStockAlerts] = useState([]);
  const [stockValuation, setStockValuation] = useState(null);
  const [departments, setDepartments] = useState(() => {
    try {
      // Initialize from localStorage if available
      const storedDepts = localStorage.getItem('stockpro_departments');
      if (storedDepts) {
        return JSON.parse(storedDepts);
      }
    } catch (error) {
      console.error('Error loading departments from localStorage:', error);
    }
    return [];
  });
  
  // Statistiques globales de l'application
  const [globalStats, setGlobalStats] = useState({
    totalProducts: 0,
    totalStock: 0,
    totalSuppliers: 0,
    totalCustomers: 0,
    totalOrders: 0,
    totalSales: 0,
    totalPurchases: 0,
    totalWarehouses: 0,
    totalReturns: 0,
    totalInvoices: 0,
    lowStockItems: 0,
    pendingOrders: 0,
    revenue: 0,
    expenses: 0,
    profit: 0
  });

  // Mettre à jour les statistiques globales
  const updateGlobalStats = (newStats) => {
    if (typeof newStats === 'function') {
      setGlobalStats(newStats);
    } else {
      setGlobalStats(prev => ({
        ...prev,
        ...newStats
      }));
    }
  };
  
  // Initialize data from localStorage on component mount
  useEffect(() => {
    // Only initialize if data doesn't exist - removed forced reset that was causing data loss
    
    // Reinitialize the localStorage with default data only if needed
    LocalStorageService.initializeDefaultData();
    
    // Load products from localStorage if available - try both keys
    let savedProducts = localStorage.getItem('stockpro_products');
    if (!savedProducts) {
      savedProducts = localStorage.getItem('products');
    }
    
    if (savedProducts) {
      try {
        const parsedProducts = JSON.parse(savedProducts);
        setProducts(parsedProducts);
        console.log('Products loaded from localStorage:', parsedProducts.length);
        
        // Make sure both storage keys have the same data
        localStorage.setItem('stockpro_products', savedProducts);
        localStorage.setItem('products', savedProducts);
      } catch (error) {
        console.error('Error loading products from localStorage:', error);
      }
    }
    
    // Load suppliers from localStorage
    const savedSuppliers = localStorage.getItem('stockpro_suppliers');
    if (savedSuppliers) {
      try {
        const parsedSuppliers = JSON.parse(savedSuppliers);
        setSuppliers(parsedSuppliers);
        console.log('Suppliers loaded from localStorage:', parsedSuppliers.length);
        updateGlobalStats(prev => ({ ...prev, totalSuppliers: parsedSuppliers.length }));
      } catch (error) {
        console.error('Error loading suppliers from localStorage:', error);
      }
    }
    
    // Load product categories from localStorage
    const savedCategories = localStorage.getItem('productCategories');
    if (savedCategories) {
      try {
        const parsedCategories = JSON.parse(savedCategories);
        setProductCategories(parsedCategories);
        console.log('Categories loaded from localStorage:', parsedCategories.length);
      } catch (error) {
        console.error('Error loading categories from localStorage:', error);
      }
    }
    
    // Load customers from API first, fallback to localStorage
    const loadCustomers = async () => {
      try {
        const response = await axios.get('http://localhost:5000/api/customers');
        const apiCustomers = response.data || [];
        setCustomers(apiCustomers);
        console.log('Customers loaded from API:', apiCustomers.length);
        
        // Save to localStorage for offline use
        localStorage.setItem('stockpro_customers', JSON.stringify(apiCustomers));
        
        updateGlobalStats(prev => ({ ...prev, totalCustomers: apiCustomers.length }));
      } catch (error) {
        console.log('API failed, loading customers from localStorage...');
        const savedCustomers = localStorage.getItem('stockpro_customers');
        if (savedCustomers) {
          try {
            const parsedCustomers = JSON.parse(savedCustomers);
            setCustomers(parsedCustomers);
            console.log('Customers loaded from localStorage:', parsedCustomers.length);
            updateGlobalStats(prev => ({ ...prev, totalCustomers: parsedCustomers.length }));
          } catch (parseError) {
            console.error('Error loading customers from localStorage:', parseError);
          }
        }
      }
    };
    
    loadCustomers();
  }, []);

  // Load subscription plans from localStorage
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  
  useEffect(() => {
    const loadSubscriptionPlans = () => {
      try {
        const savedPlans = localStorage.getItem('stockpro_subscriptionPlans') || localStorage.getItem('subscriptionPlans');
        if (savedPlans) {
          const parsedPlans = JSON.parse(savedPlans);
          setSubscriptionPlans(parsedPlans);
          console.log('Subscription plans loaded from localStorage:', parsedPlans.length);
        } else {
          // Initialize with empty array if no data exists
          setSubscriptionPlans([]);
          localStorage.setItem('stockpro_subscriptionPlans', JSON.stringify([]));
        }
      } catch (error) {
        console.error('Error loading subscription plans:', error);
        setSubscriptionPlans([]);
      }
    };
    
    loadSubscriptionPlans();
  }, []);
  
  // Load subscriptions from localStorage
  const [subscriptions, setSubscriptions] = useState([]);
  
  useEffect(() => {
    const loadSubscriptions = () => {
      try {
        const savedSubscriptions = localStorage.getItem('stockpro_subscriptions') || localStorage.getItem('subscriptions');
        if (savedSubscriptions) {
          const parsedSubscriptions = JSON.parse(savedSubscriptions);
          setSubscriptions(parsedSubscriptions);
          console.log('Subscriptions loaded from localStorage:', parsedSubscriptions.length);
        } else {
          // Initialize with empty array if no data exists
          setSubscriptions([]);
          localStorage.setItem('stockpro_subscriptions', JSON.stringify([]));
        }
      } catch (error) {
        console.error('Error loading subscriptions:', error);
        setSubscriptions([]);
      }
    };
    
    loadSubscriptions();
  }, []);

  // Load notifications from localStorage
  useEffect(() => {
    const loadNotifications = () => {
      try {
        const savedNotifications = localStorage.getItem('stockpro_notifications') || localStorage.getItem('notifications');
        if (savedNotifications) {
          const parsedNotifications = JSON.parse(savedNotifications);
          // Convert string timestamps back to Date objects
          const processedNotifications = parsedNotifications.map(notif => ({
            ...notif,
            timestamp: notif.timestamp ? new Date(notif.timestamp) : new Date()
          }));
          setNotifications(processedNotifications);
          setUnreadCount(processedNotifications.filter(n => !n.read).length);
          console.log('Notifications loaded from localStorage:', processedNotifications.length);
        } else {
          // Initialize with empty array if no notifications exist
          setNotifications([]);
          setUnreadCount(0);
          localStorage.setItem('stockpro_notifications', JSON.stringify([]));
        }
      } catch (error) {
        console.error('Error loading notifications:', error);
        setNotifications([]);
        setUnreadCount(0);
      }
    };
    
    loadNotifications();
  }, []);
  
  // Add a new notification and persist to localStorage
  const addNotification = (notification) => {
    const newNotification = {
      id: Date.now(),
      timestamp: new Date(),
      read: false,
      ...notification
    };
    
    setNotifications(prev => {
      const updatedNotifications = [newNotification, ...prev];
      
      // Persist to localStorage
      try {
        localStorage.setItem('stockpro_notifications', JSON.stringify(updatedNotifications));
      } catch (error) {
        console.error('Error saving notifications to localStorage:', error);
      }
      
      return updatedNotifications;
    });
    
    setUnreadCount(prev => prev + 1);
    
    // Show toast for incoming notifications
    toast(
      <div>
        <strong>{newNotification.title}</strong>
        <p className="text-sm">{newNotification.message}</p>
      </div>,
      {
        icon: newNotification.type === 'warning' ? '⚠️' : 
               newNotification.type === 'error' ? '❌' : 
               newNotification.type === 'success' ? '✅' : 'ℹ️',
        duration: 4000
      }
    );
    
    return newNotification.id;
  };

  // Mark notification as read and persist to localStorage
  const markAsRead = (notificationId) => {
    setNotifications(prev => {
      const updatedNotifications = prev.map(notif => 
        notif.id === notificationId ? { ...notif, read: true } : notif
      );
      
      // Persist to localStorage
      try {
        localStorage.setItem('stockpro_notifications', JSON.stringify(updatedNotifications));
      } catch (error) {
        console.error('Error saving notifications to localStorage:', error);
      }
      
      return updatedNotifications;
    });
    
    setUnreadCount(prev => Math.max(0, prev - 1));
  };

  // Fetch all data (simulates API calls)
  const fetchAllData = useCallback(async () => {
    // Prevent multiple simultaneous calls
    if (isLoadingAppData) {
      return;
    }
    
    setIsLoadingAppData(true);
    try {
      console.log('Fetching all data...');
      let fetchedData = {
        products: [],
        suppliers: [],
        customers: [],
        categories: [],
        purchases: [],
        sales: [],
        returns: []
      };
      
      // Load products from API first, fallback to localStorage
      try {
        // Import the API client
        const api = (await import('../services/api')).default;
        
        const response = await api.get('/products');
        const apiProducts = response.data || [];
        setProducts(apiProducts);
        console.log('Products loaded from API:', apiProducts.length);
        fetchedData.products = apiProducts;
        
        // Save to localStorage for offline use
        localStorage.setItem('stockpro_products', JSON.stringify(apiProducts));
      } catch (error) {
        console.log('API failed, loading products from localStorage...', error);
        let savedProducts = localStorage.getItem('stockpro_products');
        if (!savedProducts) {
          savedProducts = localStorage.getItem('products');
        }
        
        if (savedProducts) {
          try {
            const parsedProducts = JSON.parse(savedProducts);
            setProducts(parsedProducts);
            console.log('Products loaded from localStorage:', parsedProducts.length);
            fetchedData.products = parsedProducts;
            
            // Save to both keys for consistency
            localStorage.setItem('stockpro_products', savedProducts);
            localStorage.setItem('products', savedProducts);
          } catch (error) {
            console.error('Error parsing products from localStorage:', error);
          }
        }
      }
      
      // Update global stats with the loaded products
      if (fetchedData.products.length > 0) {
        const productCount = fetchedData.products.length;
        const stockValue = fetchedData.products.reduce((sum, product) => {
          const price = parseFloat(product.price) || 0;
          const stock = parseInt(product.stock) || 0;
          return sum + (price * stock);
        }, 0);
        
        // Extract categories from products if none are explicitly stored
        const productsCategories = [...new Set(fetchedData.products.map(product => product.category))].filter(Boolean);
        if (productsCategories.length > 0) {
          setProductCategories(prev => prev.length ? prev : productsCategories);
          fetchedData.categories = productsCategories;
        }
        
        updateGlobalStats(prev => ({
          ...prev,
          totalProducts: productCount,
          totalStock: stockValue
        }));
      }
      
      // Load suppliers from API first, fallback to localStorage
      try {
        const response = await axios.get('http://localhost:5000/api/suppliers');
        const apiSuppliers = response.data || [];
        setSuppliers(apiSuppliers);
        console.log('Suppliers loaded from API:', apiSuppliers.length);
        fetchedData.suppliers = apiSuppliers;
        
        // Save to localStorage for offline use
        localStorage.setItem('stockpro_suppliers', JSON.stringify(apiSuppliers));
        
        updateGlobalStats(prev => ({ 
          ...prev,
          totalSuppliers: apiSuppliers.length 
        }));
      } catch (error) {
        console.log('API failed, loading suppliers from localStorage...');
        const savedSuppliers = localStorage.getItem('stockpro_suppliers');
        if (savedSuppliers) {
          try {
            const parsedSuppliers = JSON.parse(savedSuppliers);
            setSuppliers(parsedSuppliers);
            console.log('Suppliers loaded from localStorage:', parsedSuppliers.length);
            fetchedData.suppliers = parsedSuppliers;
            
            updateGlobalStats(prev => ({ 
              ...prev,
              totalSuppliers: parsedSuppliers.length 
            }));
          } catch (parseError) {
            console.error('Error parsing suppliers from localStorage:', parseError);
          }
        }
      }
      
      // Load customers from API first, fallback to localStorage
      try {
        const response = await axios.get('http://localhost:5000/api/customers');
        const apiCustomers = response.data || [];
        setCustomers(apiCustomers);
        console.log('Customers loaded from API (fetchAllData):', apiCustomers.length);
        fetchedData.customers = apiCustomers;
        
        // Save to localStorage for offline use
        localStorage.setItem('stockpro_customers', JSON.stringify(apiCustomers));
        
        updateGlobalStats(prev => ({ 
          ...prev,
          totalCustomers: apiCustomers.length 
        }));
      } catch (error) {
        console.log('API failed, loading customers from localStorage (fetchAllData)...');
        const savedCustomers = localStorage.getItem('stockpro_customers');
        if (savedCustomers) {
          try {
            const parsedCustomers = JSON.parse(savedCustomers);
            setCustomers(parsedCustomers);
            console.log('Customers loaded from localStorage (fetchAllData):', parsedCustomers.length);
            fetchedData.customers = parsedCustomers;
            
            updateGlobalStats(prev => ({ 
              ...prev,
              totalCustomers: parsedCustomers.length 
            }));
          } catch (parseError) {
            console.error('Error parsing customers from localStorage:', parseError);
          }
        }
      }
      
      // Load categories from localStorage
      const savedCategories = localStorage.getItem('productCategories');
      if (savedCategories) {
        try {
          const parsedCategories = JSON.parse(savedCategories);
          setProductCategories(parsedCategories);
          console.log('Categories loaded from localStorage:', parsedCategories.length);
          fetchedData.categories = parsedCategories;
        } catch (error) {
          console.error('Error parsing categories from localStorage:', error);
        }
      }
      
      // Load purchases from localStorage
      const savedPurchases = localStorage.getItem('stockpro_purchases');
      if (savedPurchases) {
        try {
          const parsedPurchases = JSON.parse(savedPurchases);
          setPurchases(parsedPurchases);
          console.log('Purchases loaded from localStorage:', parsedPurchases.length);
          fetchedData.purchases = parsedPurchases;
          
          updateGlobalStats(prev => ({ 
            ...prev,
            totalPurchases: parsedPurchases.length 
          }));
        } catch (error) {
          console.error('Error parsing purchases from localStorage:', error);
        }
      }
      
      // Load sales from localStorage
      const savedSales = localStorage.getItem('stockpro_sales');
      if (savedSales) {
        try {
          const parsedSales = JSON.parse(savedSales);
          setSales(parsedSales);
          console.log('Sales loaded from localStorage:', parsedSales.length);
          fetchedData.sales = parsedSales;
          
          updateGlobalStats(prev => ({ 
            ...prev,
            totalSales: parsedSales.length 
          }));
        } catch (error) {
          console.error('Error parsing sales from localStorage:', error);
        }
      }
      
      // Load returns from localStorage
      const savedReturns = localStorage.getItem('stockpro_returns');
      if (savedReturns) {
        try {
          const parsedReturns = JSON.parse(savedReturns);
          setReturns(parsedReturns);
          console.log('Returns loaded from localStorage:', parsedReturns.length);
          fetchedData.returns = parsedReturns;
          
          updateGlobalStats(prev => ({ 
            ...prev,
            totalReturns: parsedReturns.length 
          }));
        } catch (error) {
          console.error('Error parsing returns from localStorage:', error);
        }
      }

      // Load inventory from API
      try {
        const apiInventory = await inventoryService.getAllInventory();
        setInventoryItems(apiInventory);
        console.log('Inventory loaded from API:', apiInventory.length);
        fetchedData.inventory = apiInventory;
      } catch (error) {
        console.log('Failed to load inventory from API:', error);
        // Fallback to empty array or create inventory from products
        const syntheticInventory = fetchedData.products.map(product => ({
          id: `inv-${product.id}`,
          productId: product.id,
          quantity: product.stock || 0,
          location: 'Entrepôt principal',
          lastUpdated: new Date().toISOString(),
          notes: 'Inventory auto-generated from product stock'
        }));
        setInventoryItems(syntheticInventory);
        fetchedData.inventory = syntheticInventory;
      }

      // Load stock movements from API with localStorage fallback/merge
      try {
        const apiStockMovements = await stockService.getStockMovements();
        
        // Get local movements from storage
        let localMovements = [];
        try {
          const savedStock = localStorage.getItem('stockpro_stock');
          if (savedStock) {
            localMovements = JSON.parse(savedStock) || [];
            console.log('Local stock movements loaded:', localMovements.length);
          }
        } catch (parseError) {
          console.error('Error parsing local stock movements:', parseError);
        }
        
        // Merge API and local data, with API taking precedence for duplicate IDs
        const apiIds = new Set(apiStockMovements.map(m => m.id));
        const uniqueLocalMovements = localMovements.filter(m => !apiIds.has(m.id));
        const combinedMovements = [...apiStockMovements, ...uniqueLocalMovements];
        
        // Sort by date (newest first)
        combinedMovements.sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
        
        setStockItems(combinedMovements);
        console.log('Combined stock movements loaded:', combinedMovements.length, 
          '(API:', apiStockMovements.length, ', Unique local:', uniqueLocalMovements.length, ')');
        
        // Save the combined list back to localStorage for persistence
        localStorage.setItem('stockpro_stock', JSON.stringify(combinedMovements));
        
        fetchedData.stockMovements = combinedMovements;
      } catch (error) {
        console.log('Failed to load stock movements from API:', error);
        
        // Fallback to localStorage if API fails
        try {
        const savedStock = localStorage.getItem('stockpro_stock');
        if (savedStock) {
            const parsedStock = JSON.parse(savedStock);
            setStockItems(parsedStock);
            fetchedData.stockMovements = parsedStock;
            console.log('Fallback: Loaded', parsedStock.length, 'movements from localStorage');
          } else {
            setStockItems([]);
            fetchedData.stockMovements = [];
            console.log('No stock movements found in localStorage');
          }
          } catch (parseError) {
            console.error('Error parsing stock from localStorage:', parseError);
          setStockItems([]);
          fetchedData.stockMovements = [];
        }
      }

      // Load stock alerts from API
      try {
        const alerts = await stockService.getStockAlerts();
        setStockAlerts(alerts);
        console.log('Stock alerts loaded from API:', alerts.length);
        
        updateGlobalStats(prev => ({
          ...prev,
          lowStockItems: alerts.length
        }));
      } catch (error) {
        console.log('Failed to load stock alerts from API:', error);
      }

      // Load stock valuation from API
      try {
        const valuation = await stockService.getStockValuation();
        setStockValuation(valuation);
        console.log('Stock valuation loaded from API');
        
        updateGlobalStats(prev => ({
          ...prev,
          totalStock: valuation.totalValue || prev.totalStock
        }));
      } catch (error) {
        console.log('Failed to load stock valuation from API:', error);
      }
      
      return fetchedData;
    } catch (error) {
      console.error('Error fetching data:', error);
      return {
        products: [],
        suppliers: [],
        customers: [],
        categories: []
      };
    } finally {
      setIsLoadingAppData(false);
    }
  }, []); // Remove updateGlobalStats dependency to prevent infinite loops
  
  // Charger toutes les données au montage du composant
  useEffect(() => {
    fetchAllData();
    // Remove toast to prevent spam - data will sync silently
    
    // Add a global refresh function to window for direct access from any component
    window.refreshAppData = async () => {
      console.log('Global app data refresh triggered');
      return await fetchAllData();
    };
    
    // Cleanup
    return () => {
      delete window.refreshAppData;
    };
  }, []);
  
  // Fonctions CRUD pour les produits
  const addProduct = async (product) => {
    try {
      // Create a copy of the product for data manipulation
      const productToSave = { ...product };
      
      // Handle image - in a real app, you would upload to server and save URL
      // Here we'll simulate by storing the image reference
      if (productToSave.image && (productToSave.image instanceof File || productToSave.image instanceof Blob)) {
        // In a real app, this would be a URL from your server after upload
        // Here we just store the image object reference for demo purposes
        productToSave.imageUrl = URL.createObjectURL(productToSave.image);
        productToSave.image = productToSave.imageUrl; // Store as string URL
      }
      
      // Generate an ID if not provided
      if (!productToSave.id) {
        const prefix = productToSave.name ? productToSave.name.slice(0, 3).toUpperCase() : 'PRD';
        const randomNum = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
        productToSave.id = `${prefix}-${randomNum}`;
      }

      // Ensure costPrice is stored as cost for compatibility with UI components
      if (productToSave.costPrice && !productToSave.cost) {
        productToSave.cost = productToSave.costPrice;
      }
      
      // Make sure numeric fields are properly parsed
      if (typeof productToSave.price === 'string') {
        productToSave.price = parseFloat(productToSave.price) || 0;
      }
      if (typeof productToSave.cost === 'string') {
        productToSave.cost = parseFloat(productToSave.cost) || 0;
      }
      if (typeof productToSave.stock === 'string') {
        productToSave.stock = parseInt(productToSave.stock) || 0;
      }
      
      // Set timestamps
      productToSave.createdAt = new Date().toISOString();
      productToSave.updatedAt = new Date().toISOString();
      
      // Try to save to API first
      try {
        const response = await axios.post('http://localhost:5000/api/products', productToSave);
        const savedProduct = response.data;
        console.log('✅ Product saved to API successfully:', savedProduct.name);
        
        // Update state with API response
        const updatedProducts = [...products, savedProduct];
        setProducts(updatedProducts);
        
        // Update product count in global stats
        updateGlobalStats(prev => ({
          ...prev,
          totalProducts: updatedProducts.length
        }));
        
        // Save to localStorage as backup
        localStorage.setItem('stockpro_products', JSON.stringify(updatedProducts));
        localStorage.setItem('products', JSON.stringify(updatedProducts));
        
        // Dispatch event for dashboard and other components to refresh
        const productAddedEvent = new CustomEvent('product-added', {
          detail: { productId: savedProduct.id }
        });
        window.dispatchEvent(productAddedEvent);
        
        // Also dispatch a general refresh event
        const refreshEvent = new CustomEvent('refresh-data');
        window.dispatchEvent(refreshEvent);
        
        return savedProduct;
      } catch (error) {
        console.error('❌ API failed, saving to localStorage only:', error.message);
        
        // Fallback to localStorage if API fails
        const updatedProducts = [...products, productToSave];
        setProducts(updatedProducts);
        
        // Update product count in global stats
        updateGlobalStats(prev => ({
          ...prev,
          totalProducts: updatedProducts.length
        }));
        
        // Save to localStorage for persistence (both keys for consistency)
        localStorage.setItem('stockpro_products', JSON.stringify(updatedProducts));
        localStorage.setItem('products', JSON.stringify(updatedProducts));
        
        // Dispatch event for dashboard and other components to refresh
        const productAddedEvent = new CustomEvent('product-added', {
          detail: { productId: productToSave.id }
        });
        window.dispatchEvent(productAddedEvent);
        
        // Also dispatch a general refresh event
        const refreshEvent = new CustomEvent('refresh-data');
        window.dispatchEvent(refreshEvent);
        
        console.log('Product added to localStorage:', productToSave);
        return productToSave;
      }
    } catch (error) {
      console.error("Error adding product:", error);
      throw error;
    }
  };
  
  const updateProduct = async (product) => {
    try {
      const productToUpdate = { ...product };
      
      // Handle image - in a real app, you would upload to server and save URL
      if (productToUpdate.image && (productToUpdate.image instanceof File || productToUpdate.image instanceof Blob)) {
        productToUpdate.imageUrl = URL.createObjectURL(productToUpdate.image);
        productToUpdate.image = productToUpdate.imageUrl; // Store as string URL
      }
      
      // Set updated timestamp
      productToUpdate.updatedAt = new Date().toISOString();
      
      // Try to update via API first
      try {
        const response = await axios.put(`http://localhost:5000/api/products/${productToUpdate.id}`, productToUpdate);
        const savedProduct = response.data;
        console.log('✅ Product updated via API successfully:', savedProduct.name);
        
        // Update state with API response
        const updatedProducts = products.map(p => 
          p.id === savedProduct.id ? savedProduct : p
        );
        
        setProducts(updatedProducts);
        
        // Save to localStorage as backup
        localStorage.setItem('stockpro_products', JSON.stringify(updatedProducts));
        localStorage.setItem('products', JSON.stringify(updatedProducts));
        
        // Dispatch event for dashboard and other components to refresh
        const productUpdatedEvent = new CustomEvent('product-updated', {
          detail: { productId: savedProduct.id }
        });
        window.dispatchEvent(productUpdatedEvent);
        
        // Also dispatch a general refresh event
        const refreshEvent = new CustomEvent('refresh-data');
        window.dispatchEvent(refreshEvent);
        
        return savedProduct;
      } catch (error) {
        console.error('❌ API update failed, saving to localStorage only:', error.message);
        
        // Fallback to localStorage if API fails
        const updatedProducts = products.map(p => 
          p.id === productToUpdate.id ? productToUpdate : p
        );
        
        setProducts(updatedProducts);
        
        // Save to localStorage for persistence (both keys for consistency)
        localStorage.setItem('stockpro_products', JSON.stringify(updatedProducts));
        localStorage.setItem('products', JSON.stringify(updatedProducts));
        
        // Dispatch event for dashboard and other components to refresh
        const productUpdatedEvent = new CustomEvent('product-updated', {
          detail: { productId: productToUpdate.id }
        });
        window.dispatchEvent(productUpdatedEvent);
        
        // Also dispatch a general refresh event
        const refreshEvent = new CustomEvent('refresh-data');
        window.dispatchEvent(refreshEvent);
        
        return productToUpdate;
      }
    } catch (error) {
      console.error("Error updating product:", error);
      throw error;
    }
  };
  
  const deleteProduct = async (id) => {
    try {
      // Try to delete via API first
      try {
        await axios.delete(`http://localhost:5000/api/products/${id}`);
        console.log('✅ Product deleted via API successfully:', id);
        
        // Update state after successful API deletion
        const updatedProducts = products.filter(item => item.id !== id);
        
        // Update state and stats
        setProducts(updatedProducts);
        updateGlobalStats(prev => ({ ...prev, totalProducts: updatedProducts.length }));
        
        // Save to localStorage as backup
        localStorage.setItem('stockpro_products', JSON.stringify(updatedProducts));
        localStorage.setItem('products', JSON.stringify(updatedProducts));
        
        // Dispatch event for dashboard and other components to refresh
        const productDeletedEvent = new CustomEvent('product-deleted', {
          detail: { productId: id }
        });
        window.dispatchEvent(productDeletedEvent);
        
        // Also dispatch a general refresh event
        const refreshEvent = new CustomEvent('refresh-data');
        window.dispatchEvent(refreshEvent);
        
        return true;
      } catch (error) {
        console.error('❌ API delete failed, deleting from localStorage only:', error.message);
        
        // Fallback to localStorage if API fails
        const updatedProducts = products.filter(item => item.id !== id);
        
        // Update state and stats
        setProducts(updatedProducts);
        updateGlobalStats(prev => ({ ...prev, totalProducts: updatedProducts.length }));
        
        // Save to localStorage for persistence (both keys for consistency)
        localStorage.setItem('stockpro_products', JSON.stringify(updatedProducts));
        localStorage.setItem('products', JSON.stringify(updatedProducts));
        
        // Dispatch event for dashboard and other components to refresh
        const productDeletedEvent = new CustomEvent('product-deleted', {
          detail: { productId: id }
        });
        window.dispatchEvent(productDeletedEvent);
        
        // Also dispatch a general refresh event
        const refreshEvent = new CustomEvent('refresh-data');
        window.dispatchEvent(refreshEvent);
        
        return true;
      }
    } catch (error) {
      console.error("Error deleting product:", error);
      return false;
    }
  };
  
  const addStockMovement = async (movement) => {
    try {
      // Check for flags that indicate this movement comes from a form
      // This helps prevent double-counting when forms call both addStockMovement and updateProductStock
      const isFromForm = movement.fromEntryForm || movement.fromExitForm;
      console.log(`Adding stock movement${isFromForm ? ' from form' : ''}:`, movement);
      
      let newMovement;
      
      try {
        // Try API first
        newMovement = await stockService.addStockMovement(movement);
      } catch (apiError) {
        console.log('API call failed for stock movement, using fallback', apiError);
        
        // Create fallback movement with proper ID
        newMovement = {
          id: movement.id || `MOV-${Date.now()}`,
          date: movement.date || new Date().toISOString(),
          ...movement
        };
        
        // Add to local storage as backup
        const currentStockItems = localStorage.getItem('stockpro_stock') 
          ? JSON.parse(localStorage.getItem('stockpro_stock')) 
          : [];
          
        const updatedStockItems = [newMovement, ...currentStockItems];
        localStorage.setItem('stockpro_stock', JSON.stringify(updatedStockItems));
        
        if (movement.type === 'entrée') {
          toast.success('Entrée de stock enregistrée localement');
        } else if (movement.type === 'sortie') {
          toast.success('Sortie de stock enregistrée localement');
        } else {
          toast.success('Mouvement de stock enregistré localement');
        }
      }
      
      // Update state only if API call was successful
      if (newMovement) {
      setStockItems(prev => [newMovement, ...prev]);
      
        // Handle items array for multi-product movements (from forms)
        if (movement.items && Array.isArray(movement.items) && movement.items.length > 0) {
          console.log('Processing multi-product movement with items:', movement.items.length);
          
          // For form submissions with multiple items, process each item
          for (const item of movement.items) {
            if (!item.id) continue;
            
            // Skip updateProductStock call if this is from a form that already calls updateProductStock
            // This prevents double-counting
            if (movement.fromEntryForm || movement.fromExitForm) {
              console.log(`Skipping stock update for product ${item.id} to prevent double-counting (from form)`);
              continue;
            }
            
            // Update stock for this item
            try {
              await updateProductStock(item.id, {
                type: movement.type,
                quantity: parseInt(item.quantity) || 0,
                warehouse: movement.warehouse || movement.location,
                reference: movement.reference,
                notes: movement.notes,
                skipMovementRecord: true // Prevent creating another movement record
              });
            } catch (itemError) {
              console.error(`Error updating stock for item ${item.id}:`, itemError);
            }
          }
        }
        // Handle single product movements
        else if (movement.productId && movement.quantity) {
          // Skip updateProductStock call if this is from a form that already calls updateProductStock
          // This prevents double-counting
          if (movement.fromEntryForm || movement.fromExitForm) {
            console.log(`Skipping stock update for product ${movement.productId} to prevent double-counting (from form)`);
          } else {
        const productToUpdate = products.find(p => p.id === movement.productId);
        
        if (productToUpdate) {
          const updatedProducts = [...products];
          const productIndex = updatedProducts.findIndex(p => p.id === movement.productId);
          
          if (productIndex !== -1) {
            const currentStock = parseInt(updatedProducts[productIndex].stock) || 0;
            let newStock = currentStock;
                
                // Debug log before update
                console.log('STOCK MOVEMENT IN ADDSTOCKMOVEMENT:', {
                  productId: movement.productId,
                  productName: updatedProducts[productIndex].name,
                  currentStock: currentStock,
                  movementType: movement.type,
                  movementQuantity: parseInt(movement.quantity) || 0
                });
            
            // Update stock based on movement type
            if (movement.type === 'entrée') {
                  newStock = currentStock + (parseInt(movement.quantity) || 0);
            } else if (movement.type === 'sortie') {
                  newStock = Math.max(0, currentStock - (parseInt(movement.quantity) || 0));
            }
            
                console.log(`STOCK UPDATE IN ADDSTOCKMOVEMENT: ${currentStock} → ${newStock} (${movement.type === 'entrée' ? '+' : '-'}${parseInt(movement.quantity) || 0})`);
                
                
                // Update warehouse association and warehouse-specific stock
            if (movement.warehouse) {
                  // Set current warehouse
              updatedProducts[productIndex].warehouseId = movement.warehouse;
                  
                  // Initialize warehouse tracking if needed
                  if (!updatedProducts[productIndex].warehouseIds) {
                    updatedProducts[productIndex].warehouseIds = [];
                  }
                  
                  // Add to warehouseIds if not already there
                  if (!updatedProducts[productIndex].warehouseIds.includes(movement.warehouse)) {
                    updatedProducts[productIndex].warehouseIds.push(movement.warehouse);
                  }
                  
                  // Initialize warehouse-specific stock tracking
                  if (!updatedProducts[productIndex].warehouseStock) {
                    updatedProducts[productIndex].warehouseStock = {};
                  }
                  
                  // Update warehouse-specific stock
                  const currentWarehouseStock = parseInt(updatedProducts[productIndex].warehouseStock[movement.warehouse] || 0);
                  let newWarehouseStock = currentWarehouseStock;
                  
                  if (movement.type === 'entrée') {
                    newWarehouseStock = currentWarehouseStock + parseInt(movement.quantity || 0);
                  } else if (movement.type === 'sortie') {
                    newWarehouseStock = Math.max(0, currentWarehouseStock - parseInt(movement.quantity || 0));
                  }
                  
                  console.log(`WAREHOUSE STOCK UPDATE: ${movement.warehouse} - ${currentWarehouseStock} → ${newWarehouseStock}`);
                  updatedProducts[productIndex].warehouseStock[movement.warehouse] = newWarehouseStock;
            }
            
            updatedProducts[productIndex].stock = newStock;
            
            // Update products state
            setProducts(updatedProducts);
            
            // Save to localStorage
            localStorage.setItem('stockpro_products', JSON.stringify(updatedProducts));
            localStorage.setItem('products', JSON.stringify(updatedProducts));
            
            console.log(`Product stock updated: ${productToUpdate.name}, New Stock: ${newStock}`);
              }
          }
        }
      }
      
      // Refresh alerts and valuation after stock movement
      try {
        const alerts = await stockService.getStockAlerts();
        setStockAlerts(alerts);
        const valuation = await stockService.getStockValuation();
        setStockValuation(valuation);
      } catch (refreshError) {
        console.log('Using local calculation for stock alerts and valuation');
        // Calculate alerts locally
        const lowStockProducts = products.filter(p => (parseInt(p.stock) || 0) <= (parseInt(p.minStockLevel) || 5));
        setStockAlerts(lowStockProducts);
        }
      }
      
      return newMovement;
    } catch (error) {
      console.error('Error adding stock movement:', error);
      toast.error('Erreur lors de l\'ajout du mouvement de stock');
      throw error;
    }
  };
  
  const deleteStockMovement = async (id) => {
    try {
      await stockService.deleteStockMovement(id);
      setStockItems(prev => prev.filter(item => item.id !== id));
      
      // Refresh alerts and valuation after deletion
      try {
        const alerts = await stockService.getStockAlerts();
        setStockAlerts(alerts);
        const valuation = await stockService.getStockValuation();
        setStockValuation(valuation);
      } catch (refreshError) {
        console.error('Error refreshing stock data after deletion:', refreshError);
      }
      
      console.log(`Stock movement ${id} deleted successfully`);
      return true;
    } catch (error) {
      console.error('Error deleting stock movement:', error);
      toast.error('Erreur lors de la suppression du mouvement de stock');
      return false;
    }
  };
  
  const updateProductStock = async (productId, movementData) => {
    try {
      console.log('AppDataContext: Delegating stock update to StockService');
      
      // Use the centralized StockService to handle the stock update
      const updatedProduct = await stockService.updateProductStock(productId, movementData);
      
      // Update the products state with the updated product
      setProducts(prevProducts => {
        const updatedProducts = [...prevProducts];
        const productIndex = updatedProducts.findIndex(p => p.id === productId);
        
        if (productIndex !== -1) {
          updatedProducts[productIndex] = updatedProduct;
        }
        
        return updatedProducts;
      });
      
      // Update stockItems state if a new movement was created
      if (!movementData.skipMovementRecord && !movementData.isTransfer) {
        // Refresh stock items from localStorage
        const stockItems = localStorage.getItem('stockpro_stock') ? 
          JSON.parse(localStorage.getItem('stockpro_stock')) : [];
        
        setStockItems(stockItems);
      }
      
      // Dispatch event to notify other components
      const stockUpdateEvent = new CustomEvent('product-stock-updated', {
        detail: { 
          productId,
          warehouse: movementData.warehouse,
          type: movementData.type,
          quantity: movementData.quantity
        }
      });
      
      window.dispatchEvent(stockUpdateEvent);
      
      // Don't refresh from API to avoid overwriting our local changes
      // This was causing the stock updates to be lost
      console.log(`Using local product data for product ${productId}`);
      // No API call here to prevent overwriting our local changes
      
      // Instead, explicitly notify that we're keeping local changes
      console.log('Preserving local product data with updated stock values');
      
      return updatedProduct;
    } catch (error) {
      console.error('Error updating product stock:', error);
      toast.error(`Erreur lors de la mise à jour du stock: ${error.message}`);
      throw error;
    }
  };
  
  const updateStockMovement = (id, updatedMovement) => {
    try {
      // Get current stock items
      const currentStockItems = localStorage.getItem('stockpro_stock') 
        ? JSON.parse(localStorage.getItem('stockpro_stock')) 
        : [];
        
      // Find the movement to update
      const index = currentStockItems.findIndex(item => item.id === id);
      if (index === -1) {
        console.error('Movement not found:', id);
        return false;
      }
      
      // Update the movement with new data
      const updatedStockItems = [...currentStockItems];
      updatedStockItems[index] = {
        ...updatedStockItems[index],
        ...updatedMovement
      };
      
      // Save to localStorage
      localStorage.setItem('stockpro_stock', JSON.stringify(updatedStockItems));
      
      // Update state
      setStockItems(updatedStockItems);
      
      // Special handling for transfers that are marked as delivered
      if (updatedMovement.type === 'transfert' && updatedMovement.status === 'delivered') {
        // Increase stock at destination location
        const productId = updatedStockItems[index].productId;
        const quantity = parseInt(updatedStockItems[index].quantity) || 0;
        
        if (productId && quantity > 0) {
          // For transfers, we've already decreased from source location when creating the transfer
          // Now we need to increase at destination when it's delivered
          updateProductStock(productId, {
            type: 'entrée',
            quantity: quantity,
            location: updatedStockItems[index].destinationLocation || 'Entrepôt Principal'
          });
        }
      }
      
      return true;
    } catch (error) {
      console.error('Error updating stock movement:', error);
      return false;
    }
  };
  
  // Recalculer les statistiques de stock
  const updateStockStats = () => {
    const updatedProducts = LocalStorageService.products.getAll();
    const totalStock = updatedProducts.reduce((sum, product) => sum + (parseInt(product.stock) || 0), 0);
    const stockValue = updatedProducts.reduce((sum, product) => sum + ((parseFloat(product.price) || 0) * (parseInt(product.stock) || 0)), 0);
    const lowStockCount = updatedProducts.filter(p => (parseInt(p.stock) || 0) <= (parseInt(p.minStockLevel) || 5)).length;
    
    updateGlobalStats(prev => ({
      ...prev,
      totalStock,
      stockValue,
      lowStockItems: lowStockCount
    }));
  };
  
  // Fonctions CRUD pour les fournisseurs
  const addSupplier = async (supplier) => {
    try {
      console.log('Adding supplier:', supplier);
      
      // Ensure required fields
      if (!supplier.name) {
        throw new Error('Supplier name is required');
      }
      
      // Add creation timestamp if not present
      if (!supplier.createdAt) {
        supplier.createdAt = new Date().toISOString();
      }
      
      // Ensure the supplier has an ID
      if (!supplier.id) {
        const prefix = 'SUP';
        const randomNum = Math.floor(Math.random() * 900) + 100; // Random number between 100-999
        supplier.id = `${prefix}-${randomNum}`;
      }
      
      // Add the supplier using the service
    const newSupplier = LocalStorageService.suppliers.add(supplier);
      
      // Also save directly to localStorage as a backup
      try {
        const currentSuppliers = JSON.parse(localStorage.getItem('stockpro_suppliers') || '[]');
        currentSuppliers.push(newSupplier);
        localStorage.setItem('stockpro_suppliers', JSON.stringify(currentSuppliers));
        localStorage.setItem('suppliers', JSON.stringify(currentSuppliers)); // Legacy key
        console.log('Supplier also saved directly to localStorage');
      } catch (storageError) {
        console.error('Error saving supplier directly to localStorage:', storageError);
      }
      
      // Update the state
      setSuppliers(prev => {
        // Make sure we don't add duplicates
        const exists = prev.some(s => String(s.id) === String(newSupplier.id));
        if (exists) {
          return prev.map(s => String(s.id) === String(newSupplier.id) ? newSupplier : s);
        } else {
          return [...prev, newSupplier];
        }
      });
      
      // Update global stats
      updateGlobalStats(prev => ({ 
        ...prev, 
        totalSuppliers: (prev.totalSuppliers || 0) + 1 
      }));
      
      console.log('Supplier added successfully:', newSupplier);
    return newSupplier;
    } catch (error) {
      console.error('Error adding supplier:', error);
      throw error;
    }
  };
  
  const updateSupplier = async (id, data) => {
    try {
      console.log('Updating supplier with ID:', id, 'Data:', data);
      
      if (!id) {
        throw new Error('Supplier ID is required for update');
      }
      
      // Add last updated timestamp
      const dataWithTimestamp = {
        ...data,
        lastUpdated: new Date().toISOString()
      };
      
      // Update the supplier using the service
      const updatedSupplier = LocalStorageService.suppliers.update(id, dataWithTimestamp);
      
      if (!updatedSupplier) {
        console.warn(`Supplier with ID ${id} not found in service, trying direct localStorage update`);
        
        // Try to update directly in localStorage as fallback
        try {
          // Get current suppliers from both possible keys
          let currentSuppliers = JSON.parse(localStorage.getItem('stockpro_suppliers') || '[]');
          if (!currentSuppliers || currentSuppliers.length === 0) {
            currentSuppliers = JSON.parse(localStorage.getItem('suppliers') || '[]');
          }
          
          // Find the supplier to update
          const index = currentSuppliers.findIndex(s => String(s.id) === String(id));
          
          if (index !== -1) {
            // Update the supplier
            currentSuppliers[index] = { 
              ...currentSuppliers[index], 
              ...dataWithTimestamp 
            };
            
            // Save back to both storage keys
            localStorage.setItem('stockpro_suppliers', JSON.stringify(currentSuppliers));
            localStorage.setItem('suppliers', JSON.stringify(currentSuppliers));
            
            // Update the state
            setSuppliers(prev => prev.map(item => 
              String(item.id) === String(id) ? currentSuppliers[index] : item
            ));
            
            console.log('Supplier updated directly in localStorage:', currentSuppliers[index]);
            return currentSuppliers[index];
          } else {
            throw new Error(`Supplier with ID ${id} not found in localStorage`);
          }
        } catch (storageError) {
          console.error('Error updating supplier directly in localStorage:', storageError);
          throw storageError;
        }
      }
      
      // Also update directly in localStorage for redundancy
      try {
        const currentSuppliers = JSON.parse(localStorage.getItem('stockpro_suppliers') || '[]');
        const index = currentSuppliers.findIndex(s => String(s.id) === String(id));
        if (index !== -1) {
          currentSuppliers[index] = updatedSupplier;
          localStorage.setItem('stockpro_suppliers', JSON.stringify(currentSuppliers));
          localStorage.setItem('suppliers', JSON.stringify(currentSuppliers)); // Legacy key
        }
      } catch (storageError) {
        console.error('Error updating supplier redundantly in localStorage:', storageError);
      }
      
      // Update the state
      setSuppliers(prev => prev.map(item => String(item.id) === String(id) ? updatedSupplier : item));
      
      console.log('Supplier updated successfully:', updatedSupplier);
    return updatedSupplier;
    } catch (error) {
      console.error('Error updating supplier:', error);
      throw error;
    }
  };
  
  const deleteSupplier = async (id) => {
    try {
      console.log('Deleting supplier with ID:', id);
      
      // Try to delete using the service
    const success = LocalStorageService.suppliers.delete(id);
      
      // Also delete directly from localStorage as a backup
      try {
        // Get current suppliers from both possible keys
        let currentSuppliers = JSON.parse(localStorage.getItem('stockpro_suppliers') || '[]');
        const filteredSuppliers = currentSuppliers.filter(s => String(s.id) !== String(id));
        
        // Save back to both storage keys
        localStorage.setItem('stockpro_suppliers', JSON.stringify(filteredSuppliers));
        localStorage.setItem('suppliers', JSON.stringify(filteredSuppliers));
        
        console.log('Supplier also deleted directly from localStorage');
      } catch (storageError) {
        console.error('Error deleting supplier directly from localStorage:', storageError);
      }
      
    if (success) {
        // Update the state
        setSuppliers(prev => prev.filter(item => String(item.id) !== String(id)));
        updateGlobalStats(prev => ({ 
          ...prev, 
          totalSuppliers: Math.max(0, (prev.totalSuppliers || 0) - 1) 
        }));
        console.log('Supplier deleted successfully');
      } else {
        console.warn('Service reported failure to delete supplier, but direct localStorage delete may have succeeded');
      }
      
    return success;
    } catch (error) {
      console.error('Error deleting supplier:', error);
      throw error;
    }
  };
  
  // Fonctions CRUD pour les clients
  const addCustomer = async (customer) => {
    try {
      // Generate an ID if not provided
      if (!customer.id) {
        const prefix = customer.firstName ? customer.firstName.slice(0, 1).toUpperCase() : 'C';
        const randomNum = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
        customer.id = `CUST-${randomNum}`;
      }
      
      // Add creation timestamp if not present
      if (!customer.createdAt) {
        customer.createdAt = new Date().toISOString().split('T')[0];
      }
      
      // Initialize customer metrics
      customer.totalTransactions = customer.totalTransactions || 0;
      customer.totalSpent = customer.totalSpent || 0;
      
      // Add to state
      const newCustomer = { ...customer };
      setCustomers(prev => [...prev, newCustomer]);
      
      // Save to localStorage
      const savedCustomers = localStorage.getItem('stockpro_customers');
      let allCustomers = [];
      if (savedCustomers) {
        allCustomers = JSON.parse(savedCustomers);
      }
      allCustomers.push(newCustomer);
      localStorage.setItem('stockpro_customers', JSON.stringify(allCustomers));
      
      // Update global stats
      updateGlobalStats(prev => ({ ...prev, totalCustomers: prev.totalCustomers + 1 || 1 }));
      console.log('Customer added successfully:', newCustomer);
      
      return newCustomer;
    } catch (error) {
      console.error('Error adding customer:', error);
      throw error;
    }
  };
  
  const updateCustomer = async (id, data) => {
    try {
      // Get current customers from localStorage
      const savedCustomers = localStorage.getItem('stockpro_customers');
      if (!savedCustomers) {
        throw new Error('No customers found in storage');
      }
      
      let allCustomers = JSON.parse(savedCustomers);
      const index = allCustomers.findIndex(c => c.id === id);
      
      if (index === -1) {
        throw new Error(`Customer with ID ${id} not found`);
      }
      
      // Update the customer
      const updatedCustomer = { ...allCustomers[index], ...data };
      allCustomers[index] = updatedCustomer;
      
      // Save to localStorage
      localStorage.setItem('stockpro_customers', JSON.stringify(allCustomers));
      
      // Update state
      setCustomers(prev => prev.map(item => item.id === id ? updatedCustomer : item));
      
      console.log('Customer updated successfully:', updatedCustomer);
      return updatedCustomer;
    } catch (error) {
      console.error('Error updating customer:', error);
      throw error;
    }
  };
  
  const deleteCustomer = async (id) => {
    try {
      // Get current customers from localStorage
      const savedCustomers = localStorage.getItem('stockpro_customers');
      if (!savedCustomers) {
        throw new Error('No customers found in storage');
      }
      
      let allCustomers = JSON.parse(savedCustomers);
      const filteredCustomers = allCustomers.filter(c => c.id !== id);
      
      if (filteredCustomers.length === allCustomers.length) {
        throw new Error(`Customer with ID ${id} not found`);
      }
      
      // Save to localStorage
      localStorage.setItem('stockpro_customers', JSON.stringify(filteredCustomers));
      
      // Update state
      setCustomers(prev => prev.filter(item => item.id !== id));
      
      // Update global stats
      updateGlobalStats(prev => ({ ...prev, totalCustomers: prev.totalCustomers - 1 }));
      
      console.log('Customer deleted successfully:', id);
      return true;
    } catch (error) {
      console.error('Error deleting customer:', error);
      return false;
    }
  };
  
  // Fonctions pour les commandes
  const addOrder = async (order) => {
    const newOrder = LocalStorageService.orders.add(order);
    setOrders(prev => [...prev, newOrder]);
    updateGlobalStats(prev => ({ ...prev, totalOrders: prev.totalOrders + 1 }));
    
    // Dispatch event for dashboard and other components to refresh
    const orderAddedEvent = new CustomEvent('order-added', {
      detail: { orderId: newOrder.id }
    });
    window.dispatchEvent(orderAddedEvent);
    
    // Also dispatch a general refresh event
    const refreshEvent = new CustomEvent('refresh-data');
    window.dispatchEvent(refreshEvent);
    
    return newOrder;
  };
  
  const updateOrder = async (id, data) => {
    const updatedOrder = LocalStorageService.orders.update(id, data);
    if (updatedOrder) {
      setOrders(prev => prev.map(item => item.id === id ? updatedOrder : item));
      
      // Dispatch event for dashboard and other components to refresh
      const orderUpdatedEvent = new CustomEvent('order-updated', {
        detail: { orderId: id }
      });
      window.dispatchEvent(orderUpdatedEvent);
      
      // Also dispatch a general refresh event
      const refreshEvent = new CustomEvent('refresh-data');
      window.dispatchEvent(refreshEvent);
    }
    return updatedOrder;
  };
  
  const deleteOrder = async (id) => {
    const success = LocalStorageService.orders.delete(id);
    if (success) {
      setOrders(prev => prev.filter(item => item.id !== id));
      updateGlobalStats(prev => ({ ...prev, totalOrders: prev.totalOrders - 1 }));
      
      // Dispatch event for dashboard and other components to refresh
      const orderDeletedEvent = new CustomEvent('order-deleted', {
        detail: { orderId: id }
      });
      window.dispatchEvent(orderDeletedEvent);
      
      // Also dispatch a general refresh event
      const refreshEvent = new CustomEvent('refresh-data');
      window.dispatchEvent(refreshEvent);
    }
    return success;
  };
  
  // Fonctions CRUD pour les catégories de produits
  const addCategory = async (category) => {
    const newCategory = LocalStorageService.categories.add(category);
    if (newCategory) {
      setProductCategories(prev => [...prev, newCategory]);
    }
    return newCategory;
  };
  
  const updateCategory = async (oldCategory, newCategory) => {
    const updatedCategory = LocalStorageService.categories.update(oldCategory, newCategory);
    if (updatedCategory) {
      setProductCategories(prev => prev.map(cat => cat === oldCategory ? newCategory : cat));
      
      // Mettre à jour les produits avec cette catégorie
      const updatedProducts = LocalStorageService.products.getAll();
      const productsWithCategory = updatedProducts.filter(p => p.category === oldCategory).map(p => ({
        ...p,
        category: newCategory
      }));
      
      productsWithCategory.forEach(p => {
        LocalStorageService.products.update(p.id, p);
      });
      
      setProducts(prev => prev.map(p => 
        p.category === oldCategory ? { ...p, category: newCategory } : p
      ));
    }
    return updatedCategory;
  };
  
  const deleteCategory = async (category) => {
    const success = LocalStorageService.categories.delete(category);
    if (success) {
      setProductCategories(prev => prev.filter(c => c !== category));
    }
    return success;
  };

  // Fonctions CRUD pour les ventes
  const addSale = async (sale) => {
    // Enhanced sale creation with proper customer name resolution
    const enrichedSale = { ...sale };
    
    // If we have a customerId but no customerName, resolve it from customers
    if (sale.customerId && !sale.customerName) {
      const customer = customers.find(c => 
        c.id == sale.customerId || 
        c.id === sale.customerId || 
        c.id === String(sale.customerId)
      );
      
      if (customer) {
        // Store the customer name in the sale for faster access in reports
        if (customer.company && customer.company.trim()) {
          enrichedSale.customerName = customer.company.trim();
        } else if (customer.name && customer.name.trim()) {
          enrichedSale.customerName = customer.name.trim();
        } else if (customer.firstName || customer.lastName) {
          const fullName = `${customer.firstName || ''} ${customer.lastName || ''}`.trim();
          if (fullName) {
            enrichedSale.customerName = fullName;
          }
        }
        
        console.log('💰 Enhanced sale with customer name:', {
          saleId: enrichedSale.id,
          customerId: sale.customerId,
          resolvedCustomerName: enrichedSale.customerName,
          customer: customer
        });
      }
    }
    
    // Add sale to storage
    const newSale = LocalStorageService.sales.add(enrichedSale);
    setSales(prev => [...prev, newSale]);
    updateGlobalStats(prev => ({ ...prev, totalSales: prev.totalSales + 1 }));
    
    // Dispatch event for dashboard and other components to refresh
    const saleAddedEvent = new CustomEvent('sale-added', {
      detail: { saleId: newSale.id }
    });
    window.dispatchEvent(saleAddedEvent);
    
    // Also dispatch a general refresh event
    const refreshEvent = new CustomEvent('refresh-data');
    window.dispatchEvent(refreshEvent);
    
    // Update customer metrics when a sale is added
    if (sale.customerId) {
      updateCustomerMetrics(sale.customerId);
    }

    // Update product stock in warehouse if warehouseId is specified
    if (enrichedSale.items && enrichedSale.items.length > 0 && enrichedSale.warehouseId) {
      try {
        // Create a single stock movement for the entire sale
        // This is more efficient than creating individual movements
        const stockMovement = {
          id: `SOR-${new Date().getFullYear()}-${Date.now().toString().slice(-6)}`,
          date: enrichedSale.date || new Date().toISOString().split('T')[0],
          type: 'sortie',
          customerId: enrichedSale.customerId,
          customerName: enrichedSale.customerName,
          reference: `Vente #${newSale.invoiceNumber || newSale.id}`,
          warehouse: enrichedSale.warehouseId,
          location: enrichedSale.warehouseId,
          notes: `Produits vendus à ${enrichedSale.customerName || 'client'}`,
          items: enrichedSale.items.map(item => ({
            id: item.id,
            name: item.name,
            quantity: parseInt(item.quantity) || 0,
            unitPrice: item.unitPrice
          })),
          fromSale: true // Flag to prevent double-counting
        };
        
        // Add the movement
        await addStockMovement(stockMovement);
        console.log(`✅ Created stock movement for sale ${newSale.id}`);
        
        // Also update each product's warehouse stock directly
        for (const item of enrichedSale.items) {
          if (item.id) {
            console.log(`🔄 Updating stock for product ${item.id} in warehouse ${enrichedSale.warehouseId} - Sale ${newSale.id}`);
            const updatedProduct = await updateProductStock(item.id, {
              type: 'sortie',
              quantity: parseInt(item.quantity) || 0,
              warehouse: enrichedSale.warehouseId,
              notes: `Vente #${newSale.invoiceNumber || newSale.id}`,
              skipMovementRecord: true // Skip creating another movement record
            });
            console.log(`✅ Stock updated for product ${item.id}: new warehouse stock:`, updatedProduct.warehouseStock);
          }
        }
        
        // Dispatch event to notify warehouse inventory page to refresh
        const warehouseUpdateEvent = new CustomEvent('warehouse-inventory-updated', {
          detail: { warehouseId: enrichedSale.warehouseId }
        });
        window.dispatchEvent(warehouseUpdateEvent);
        
        // Also dispatch a general refresh event
        const refreshEvent = new CustomEvent('refresh-data');
        window.dispatchEvent(refreshEvent);
        
        console.log(`✅ Updated warehouse stock for all products in sale ${newSale.id}`);
      } catch (error) {
        console.error('❌ Error updating warehouse stock during sale:', error);
      }
    }
    
    return newSale;
  };
  
  // Function to update customer metrics based on sales and orders
  const updateCustomerMetrics = async (customerId) => {
    try {
      // Get current sales
      const allSales = sales.length > 0 ? sales : LocalStorageService.sales.getAll() || [];
      
      // Get current customers
      const savedCustomers = localStorage.getItem('stockpro_customers');
      if (!savedCustomers) return;
      
      let allCustomers = JSON.parse(savedCustomers);
      const customerIndex = allCustomers.findIndex(c => c.id === customerId);
      
      if (customerIndex === -1) return;
      
      // Filter sales for this customer
      const customerSales = allSales.filter(sale => sale.customerId === customerId);
      
      // Calculate metrics
      const totalTransactions = customerSales.length;
      const totalSpent = customerSales.reduce((total, sale) => total + (parseFloat(sale.total) || 0), 0);
      const lastTransaction = customerSales.length > 0 
        ? customerSales.sort((a, b) => new Date(b.date) - new Date(a.date))[0].date
        : allCustomers[customerIndex].lastTransaction;
      
      // Update customer
      const updatedCustomer = {
        ...allCustomers[customerIndex],
        totalTransactions,
        totalSpent,
        lastTransaction
      };
      
      allCustomers[customerIndex] = updatedCustomer;
      
      // Save to localStorage
      localStorage.setItem('stockpro_customers', JSON.stringify(allCustomers));
      
      // Update state
      setCustomers(prev => prev.map(item => item.id === customerId ? updatedCustomer : item));
      
      console.log('Customer metrics updated:', customerId);
    } catch (error) {
      console.error('Error updating customer metrics:', error);
    }
  };
  
  const updateSale = async (id, data) => {
    // Find the original sale first to get the customer ID
    const originalSale = sales.find(s => s.id === id) || LocalStorageService.sales.getById(id);
    const originalCustomerId = originalSale ? originalSale.customerId : null;
    
    const updatedSale = LocalStorageService.sales.update(id, data);
    if (updatedSale) {
      setSales(prev => prev.map(item => item.id === id ? updatedSale : item));
      
      // Dispatch event for dashboard and other components to refresh
      const saleUpdatedEvent = new CustomEvent('sale-updated', {
        detail: { saleId: id }
      });
      window.dispatchEvent(saleUpdatedEvent);
      
      // Also dispatch a general refresh event
      const refreshEvent = new CustomEvent('refresh-data');
      window.dispatchEvent(refreshEvent);
      
      // Update customer metrics if customer ID changed or remained the same
      if (originalCustomerId) {
        updateCustomerMetrics(originalCustomerId);
      }
      
      // If customer ID changed, update metrics for new customer too
      if (updatedSale.customerId && updatedSale.customerId !== originalCustomerId) {
        updateCustomerMetrics(updatedSale.customerId);
      }
    }
    return updatedSale;
  };
  
  const deleteSale = async (id) => {
    // Find the sale first to get the customer ID
    const saleToDelete = sales.find(s => s.id === id) || LocalStorageService.sales.getById(id);
    const customerIdToUpdate = saleToDelete ? saleToDelete.customerId : null;
    
    const success = LocalStorageService.sales.delete(id);
    if (success) {
      setSales(prev => prev.filter(item => item.id !== id));
      updateGlobalStats(prev => ({ ...prev, totalSales: prev.totalSales - 1 }));
      
      // Update customer metrics if there was a customer associated with this sale
      if (customerIdToUpdate) {
        updateCustomerMetrics(customerIdToUpdate);
      }
    }
    return success;
  };

  // Fonctions CRUD pour les achats
  const addPurchase = async (purchase) => {
    // Add purchase to storage
    const newPurchase = LocalStorageService.purchases.add(purchase);
    setPurchases(prev => [...prev, newPurchase]);
    updateGlobalStats(prev => ({ ...prev, totalPurchases: prev.totalPurchases + 1 }));
    
    // Dispatch event for dashboard and other components to refresh
    const purchaseAddedEvent = new CustomEvent('purchase-added', {
      detail: { purchaseId: newPurchase.id }
    });
    window.dispatchEvent(purchaseAddedEvent);
    
    // Also dispatch a general refresh event
    const refreshEvent = new CustomEvent('refresh-data');
    window.dispatchEvent(refreshEvent);
    
    // Update product stock in warehouse if warehouseId is specified
    if (purchase.items && purchase.items.length > 0 && purchase.warehouseId) {
      try {
        // Create a single stock movement for the entire purchase
        // This is more efficient than creating individual movements
        const stockMovement = {
          id: `ENT-${new Date().getFullYear()}-${Date.now().toString().slice(-6)}`,
          date: purchase.date || new Date().toISOString().split('T')[0],
          type: 'entrée',
          supplierId: purchase.supplierId,
          supplierName: purchase.supplierName,
          reference: `Achat #${newPurchase.purchaseNumber || newPurchase.id}`,
          warehouse: purchase.warehouseId,
          location: purchase.warehouseId,
          notes: `Produits reçus du fournisseur ${purchase.supplierName || 'inconnu'}`,
          items: purchase.items.map(item => ({
            id: item.id,
            name: item.name,
            quantity: parseInt(item.quantity) || 0,
            unitPrice: item.unitPrice
          })),
          fromPurchase: true // Flag to prevent double-counting
        };
        
        // Add the movement
        await addStockMovement(stockMovement);
        console.log(`✅ Created stock movement for purchase ${newPurchase.id}`);
        
        // Also update each product's warehouse stock directly
        for (const item of purchase.items) {
          if (item.id) {
            console.log(`🔄 Updating stock for product ${item.id} in warehouse ${purchase.warehouseId} - Purchase ${newPurchase.id}`);
            const updatedProduct = await updateProductStock(item.id, {
              type: 'entrée',
              quantity: parseInt(item.quantity) || 0,
              warehouse: purchase.warehouseId,
              notes: `Achat #${newPurchase.purchaseNumber || newPurchase.id}`,
              skipMovementRecord: true // Skip creating another movement record
            });
            console.log(`✅ Stock updated for product ${item.id}: new warehouse stock:`, updatedProduct.warehouseStock);
          }
        }
        
        // Dispatch event to notify warehouse inventory page to refresh
        const warehouseUpdateEvent = new CustomEvent('warehouse-inventory-updated', {
          detail: { warehouseId: purchase.warehouseId }
        });
        window.dispatchEvent(warehouseUpdateEvent);
        
        // Also dispatch a general refresh event
        const refreshEvent = new CustomEvent('refresh-data');
        window.dispatchEvent(refreshEvent);
        
        console.log(`✅ Updated warehouse stock for all products in purchase ${newPurchase.id}`);
      } catch (error) {
        console.error('❌ Error updating warehouse stock during purchase:', error);
      }
    }
    
    return newPurchase;
  };
  
  const updatePurchase = async (id, data) => {
    const updatedPurchase = LocalStorageService.purchases.update(id, data);
    if (updatedPurchase) {
      setPurchases(prev => prev.map(item => item.id === id ? updatedPurchase : item));
      
      // Dispatch event for dashboard and other components to refresh
      const purchaseUpdatedEvent = new CustomEvent('purchase-updated', {
        detail: { purchaseId: id }
      });
      window.dispatchEvent(purchaseUpdatedEvent);
      
      // Also dispatch a general refresh event
      const refreshEvent = new CustomEvent('refresh-data');
      window.dispatchEvent(refreshEvent);
    }
    return updatedPurchase;
  };
  
  const deletePurchase = async (id) => {
    const success = LocalStorageService.purchases.delete(id);
    if (success) {
      setPurchases(prev => prev.filter(item => item.id !== id));
      updateGlobalStats(prev => ({ ...prev, totalPurchases: prev.totalPurchases - 1 }));
    }
    return success;
  };

  // Fonctions CRUD pour les entrepôts
  const addWarehouse = async (warehouse) => {
    const newWarehouse = LocalStorageService.warehouses.add(warehouse);
    setWarehouses(prev => [...prev, newWarehouse]);
    updateGlobalStats(prev => ({ ...prev, totalWarehouses: prev.totalWarehouses + 1 }));
    return newWarehouse;
  };
  
  const updateWarehouse = async (id, data) => {
    const updatedWarehouse = LocalStorageService.warehouses.update(id, data);
    if (updatedWarehouse) {
      setWarehouses(prev => prev.map(item => item.id === id ? updatedWarehouse : item));
    }
    return updatedWarehouse;
  };
  
  const deleteWarehouse = async (id) => {
    const success = LocalStorageService.warehouses.delete(id);
    if (success) {
      setWarehouses(prev => prev.filter(item => item.id !== id));
      updateGlobalStats(prev => ({ ...prev, totalWarehouses: prev.totalWarehouses - 1 }));
    }
    return success;
  };

  // Fonctions CRUD pour les retours
  const addReturn = async (returnItem) => {
    const newReturn = LocalStorageService.returns.add(returnItem);
    setReturns(prev => [...prev, newReturn]);
    updateGlobalStats(prev => ({ ...prev, totalReturns: prev.totalReturns + 1 }));
    return newReturn;
  };
  
  const updateReturn = async (id, data) => {
    const updatedReturn = LocalStorageService.returns.update(id, data);
    if (updatedReturn) {
      setReturns(prev => prev.map(item => item.id === id ? updatedReturn : item));
    }
    return updatedReturn;
  };
  
  const deleteReturn = async (id) => {
    const success = LocalStorageService.returns.delete(id);
    if (success) {
      setReturns(prev => prev.filter(item => item.id !== id));
      updateGlobalStats(prev => ({ ...prev, totalReturns: prev.totalReturns - 1 }));
    }
    return success;
  };

  // Fonctions pour les employés
  const addEmployee = async (employee) => {
    try {
      const storedEmployees = JSON.parse(localStorage.getItem('stockpro_employees') || '[]');
      const newEmployee = { id: `EMP-${String(Math.floor(Math.random() * 1000)).padStart(3, '0')}`, ...employee };
      const updatedEmployees = [...storedEmployees, newEmployee];
      
      localStorage.setItem('stockpro_employees', JSON.stringify(updatedEmployees));
      return newEmployee;
    } catch (error) {
      console.error('Error adding employee:', error);
      return null;
    }
  };
  
  const updateEmployee = async (id, data) => {
    try {
      const storedEmployees = JSON.parse(localStorage.getItem('stockpro_employees') || '[]');
      const updatedEmployees = storedEmployees.map(emp => emp.id === id ? { ...emp, ...data } : emp);
      
      localStorage.setItem('stockpro_employees', JSON.stringify(updatedEmployees));
      return { id, ...data };
    } catch (error) {
      console.error('Error updating employee:', error);
      return null;
    }
  };
  
  const deleteEmployee = async (id) => {
    try {
      const storedEmployees = JSON.parse(localStorage.getItem('stockpro_employees') || '[]');
      const updatedEmployees = storedEmployees.filter(employee => employee.id !== id);
      
      localStorage.setItem('stockpro_employees', JSON.stringify(updatedEmployees));
      return true;
    } catch (error) {
      console.error('Error deleting employee:', error);
      return false;
    }
  };

  const getEmployees = () => {
    // Get employees from localStorage if available
    const storedEmployees = localStorage.getItem('stockpro_employees');
    if (storedEmployees) {
      try {
        return JSON.parse(storedEmployees);
      } catch (error) {
        console.error('Error parsing employees from localStorage:', error);
      }
    }
    return [];
  };
  
  const getEmployeeById = (id) => {
    const employees = getEmployees();
    return employees.find(emp => emp.id === id);
  };

  // Exposer le contexte
  const value = {
    // Données
    selectedMember,
    setSelectedMember,
    selectedEmployee,
    setSelectedEmployee,
    selectedProduct,
    setSelectedProduct,
    notifications,
    unreadCount,
    globalStats,
    
    // Données partagées entre sections
    products,
    suppliers,
    customers,
    orders,
    sales,
    purchases,
    warehouses,
    returns,
    invoices,
    stockItems,
    productCategories,
    inventoryItems,
    stockAlerts,
    stockValuation,
    
    // Fonctions de chargement et mise à jour
    fetchAllData,
    updateGlobalStats,
    
    // Fonctions CRUD produits
    addProduct,
    updateProduct,
    deleteProduct,
    getProducts: () => {
      // If products are already loaded in state, return them
      if (products && products.length > 0) {
        return products;
      }
      
      // Try to load products from localStorage
      let savedProducts = localStorage.getItem('stockpro_products');
      if (!savedProducts) {
        savedProducts = localStorage.getItem('products');
      }
      
      if (savedProducts) {
        try {
          const parsedProducts = JSON.parse(savedProducts);
          return parsedProducts;
        } catch (error) {
          console.error('Error loading products in getProducts:', error);
        }
      }
      
      return [];
    },
    getProductById: (id) => products.find(product => product.id === id),
    getLowStockProducts: () => products.filter(product => product.stock <= (product.lowStockThreshold || 5)),
    
    // Fonctions CRUD fournisseurs
    addSupplier,
    updateSupplier,
    deleteSupplier,
    getSuppliers: () => suppliers,
    getSupplierById: (id) => suppliers.find(supplier => supplier.id === id),
    
    // Fonctions CRUD clients
    addCustomer,
    updateCustomer,
    deleteCustomer,
    getCustomers: () => {
      // If customers are already loaded in state, return them
      if (customers && customers.length > 0) {
        return customers;
      }
      // Try to load from localStorage (both historical and current keys)
      let saved = localStorage.getItem('stockpro_customers');
      if (!saved) {
        saved = localStorage.getItem('customers');
      }
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          return Array.isArray(parsed) ? parsed : [];
        } catch (e) {
          console.error('Error loading customers in getCustomers:', e);
        }
      }
      return [];
    },
    getCustomerById: (id) => {
      const list = customers && customers.length > 0 ? customers : (() => {
        let saved = localStorage.getItem('stockpro_customers') || localStorage.getItem('customers');
        try { return saved ? JSON.parse(saved) : []; } catch { return []; }
      })();
      return list.find(customer => customer.id === id);
    },
    
    // Fonctions CRUD commandes
    addOrder,
    updateOrder,
    deleteOrder,
    getOrders: () => orders,
    getOrderById: (id) => orders.find(order => order.id === id),
    getPendingOrders: () => orders.filter(order => order.status === 'en cours' || order.status === 'en attente'),
    
    // Fonctions ventes
    addSale,
    updateSale,
    deleteSale,
    getSales: () => sales,
    getSaleById: (id) => sales.find(sale => sale.id === id),
    
    // Fonctions achats
    addPurchase,
    updatePurchase,
    deletePurchase,
    getPurchases: () => purchases,
    getPurchaseById: (id) => purchases.find(purchase => purchase.id === id),
    
    // Fonctions entrepôts
    addWarehouse,
    updateWarehouse,
    deleteWarehouse,
    getWarehouses: () => warehouses,
    getWarehouseById: (id) => warehouses.find(warehouse => warehouse.id === id),
    
    // Fonctions retours
    addReturn,
    updateReturn,
    deleteReturn,
    getReturns: () => returns,
    getReturnById: (id) => returns.find(returnItem => returnItem.id === id),
    
    // Fonctions employés
    addEmployee,
    updateEmployee,
    deleteEmployee,
    getEmployees,
    getEmployeeById,
    
    // Fonctions départements - now using direct localStorage management
    addDepartment: (departmentName) => {
      try {
        // Get current departments from localStorage
        const storedDepts = localStorage.getItem('stockpro_departments');
        const departments = storedDepts ? JSON.parse(storedDepts) : [];
        
        // Add new department
        const updatedDepartments = [...departments, departmentName];
        
        // Save back to localStorage
        localStorage.setItem('stockpro_departments', JSON.stringify(updatedDepartments));
        
        // Update state
        setDepartments(updatedDepartments);
        
        console.log('Department added to context:', departmentName);
        return true;
      } catch (error) {
        console.error('Error adding department:', error);
        return false;
      }
    },
    updateDepartment: (oldName, newName) => {
      try {
        // Update employee departments when department name changes
        const storedEmployees = localStorage.getItem('stockpro_employees');
        if (storedEmployees) {
          const employees = JSON.parse(storedEmployees);
          const updatedEmployees = employees.map(employee => 
            employee.department === oldName ? { ...employee, department: newName } : employee
          );
          localStorage.setItem('stockpro_employees', JSON.stringify(updatedEmployees));
        }
        
        // Update departments
        const storedDepts = localStorage.getItem('stockpro_departments');
        if (storedDepts) {
          const departments = JSON.parse(storedDepts);
          const updatedDepts = departments.map(dept => 
            dept === oldName ? newName : dept
          );
          localStorage.setItem('stockpro_departments', JSON.stringify(updatedDepts));
          
          // Update state
          setDepartments(updatedDepts);
        }
        
        console.log('Department updated in context:', oldName, '->', newName);
        return true;
      } catch (error) {
        console.error('Error updating department:', error);
        return false;
      }
    },
    deleteDepartment: (departmentName) => {
      try {
        // Check if any employees are using this department
        const storedEmployees = localStorage.getItem('stockpro_employees');
        if (storedEmployees) {
          const employees = JSON.parse(storedEmployees);
          if (employees.some(emp => emp.department === departmentName)) {
            console.log('Cannot delete department in use:', departmentName);
            return false; // Can't delete if in use
          }
        }
        
        // Delete from departments
        const storedDepts = localStorage.getItem('stockpro_departments');
        if (storedDepts) {
          const departments = JSON.parse(storedDepts);
          const updatedDepts = departments.filter(dept => dept !== departmentName);
          localStorage.setItem('stockpro_departments', JSON.stringify(updatedDepts));
          
          // Update state
          setDepartments(updatedDepts);
        }
        
        console.log('Department deleted from context:', departmentName);
        return true;
      } catch (error) {
        console.error('Error deleting department:', error);
        return false;
      }
    },
    
    // Fonctions stock
    addStockMovement,
    updateProductStock,
  deleteStockMovement,
  updateStockMovement,
  getStockMovements: () => stockItems,
  getStockItems: () => stockItems,
    
    // Transfer stock between warehouses
    transferStock: async (transferData) => {
      try {
        console.log('AppDataContext: Delegating transfer to StockService');
        
        // Use the centralized StockService to handle the transfer
        const result = await stockService.transferStock(transferData);
        
        // Update the stockItems state with the new transfer record
        setStockItems(prevItems => [result, ...prevItems]);
        
        // Update products state with the updated products
        if (result.processedItems && result.processedItems.length > 0) {
          // Refresh products from localStorage to ensure we have the latest data
          const updatedProducts = localStorage.getItem('products') ? 
            JSON.parse(localStorage.getItem('products')) : [];
          
          setProducts(updatedProducts);
        }
        
        return result;
      } catch (error) {
        console.error('Error transferring stock:', error);
        toast.error(`Erreur lors du transfert: ${error.message}`);
        throw error;
      }
    },
  
  // Get product stock by warehouse
  getProductStockByWarehouse: (productId, warehouseId) => {
    try {
      const product = products.find(p => p.id === productId);
      if (!product) return 0;
      
      // If the product is not in this warehouse, return 0
      if (product.warehouseIds && !product.warehouseIds.includes(warehouseId)) {
        return 0;
      }
      
      // If we have warehouse-specific stock tracking, use that
      if (product.warehouseStock && product.warehouseStock[warehouseId] !== undefined) {
        return parseInt(product.warehouseStock[warehouseId]) || 0;
      }
      
      // Calculate stock from movements if warehouse-specific stock is not available
      let stock = 0;
      
      // Look through stock movements for this product and warehouse
      if (stockItems && stockItems.length > 0) {
        const relevantMovements = stockItems.filter(item => 
          item.productId === productId && 
          (item.warehouse === warehouseId || item.location === warehouseId)
        );
        
        relevantMovements.forEach(movement => {
          const quantity = parseInt(movement.quantity) || 0;
          if (movement.type === 'entrée') {
            stock += quantity;
          } else if (movement.type === 'sortie') {
            stock -= quantity;
          }
        });
      }
      
      // Ensure stock is not negative
      return Math.max(0, stock);
    } catch (error) {
      console.error('Error getting product stock by warehouse:', error);
      return 0;
    }
  },
  
    // Fonctions inventaire
    getInventoryItems: () => inventoryItems,
    getStockAlerts: () => stockAlerts,
    getStockValuation: () => stockValuation,
    refreshInventoryData: async () => {
      try {
        const [inventory, alerts, valuation] = await Promise.all([
          inventoryService.getAllInventory(),
          stockService.getStockAlerts(),
          stockService.getStockValuation()
        ]);
        setInventoryItems(inventory);
        setStockAlerts(alerts);
        setStockValuation(valuation);
        return { inventory, alerts, valuation };
      } catch (error) {
        console.error('Error refreshing inventory data:', error);
        throw error;
      }
    },
    
    // Fonctions catégories
    addCategory,
    updateCategory,
    deleteCategory,
    getCategories: () => productCategories,
    
    // Fonctions notifications
    addNotification,
    markAsRead
  };

  return (
    <AppDataContext.Provider value={value}>
      {children}
    </AppDataContext.Provider>
  );
};